import { type ReactElement, useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';

export interface MagneticProps {
  /** A single element child that receives the magnetic transform. */
  children: ReactElement;
  /** How far the element is pulled toward the pointer (0–1 of offset). */
  strength?: number;
  /** Radius (px) around the element within which the pull engages. */
  radius?: number;
}

/**
 * Wraps a single interactive element so it is gently drawn toward the pointer —
 * a tactile, high-touch cue reserved for primary CTAs and key controls. Uses a
 * quickTo for buttery 60fps tracking. Disabled entirely for reduced-motion and
 * on touch (no hover) devices, where it would never fire anyway.
 */
export function Magnetic({ children, strength = 0.35, radius = 120 }: MagneticProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || reduced) return;
      if (window.matchMedia('(hover: none)').matches) return;

      const xTo = gsap.quickTo(el, 'x', { duration: DURATION.fast, ease: GSAP_EASE.spring });
      const yTo = gsap.quickTo(el, 'y', { duration: DURATION.fast, ease: GSAP_EASE.spring });

      const onMove = (event: PointerEvent) => {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = event.clientX - cx;
        const dy = event.clientY - cy;
        const distance = Math.hypot(dx, dy);
        if (distance < radius + Math.max(rect.width, rect.height) / 2) {
          xTo(dx * strength);
          yTo(dy * strength);
        } else {
          xTo(0);
          yTo(0);
        }
      };
      const onLeave = () => {
        xTo(0);
        yTo(0);
      };

      window.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      return () => {
        window.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      };
    },
    { scope: ref, dependencies: [reduced, strength, radius] },
  );

  return (
    <span ref={ref} className="inline-block will-change-transform">
      {children}
    </span>
  );
}
